/**
 * @file SeanceCrossfit.tsx
 * @summary Article de blog — SeanceCrossfit
 *
 * Page d'article TSX du silo SEO/GEO. Inclut métadonnées (SEO), JSON-LD, et CTA Resawod. Inscriptions cours via Resawod uniquement (sauf séances d'essai).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import SEO from "@/components/SEO";
import BlogHeroImage, { getArticleImageUrls, withArticleImages, slugFromUrl } from "@/components/blog/BlogHeroImage";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { ChevronRight, ArrowLeft, Activity, Flame, Dumbbell, Timer, Users, Calculator, BookOpen, Target } from "lucide-react";
import BlogCTA, { BlogStickyCTA } from "@/components/blog/BlogCTA";

const URL = "https://crossfitchistera.fr/blog/comment-se-passe-une-seance-crossfit";

const SLUG = slugFromUrl(URL);
const IMAGES = getArticleImageUrls(SLUG, 5);
const faq = [
  { "@type": "Question", name: "Une séance dure combien de temps ?", acceptedAnswer: { "@type": "Answer", text: "En général environ une heure, briefing, warm up, skill/renforcement, WOD et récupération inclus." } },
  { "@type": "Question", name: "Peut-on venir débutant ?", acceptedAnswer: { "@type": "Answer", text: "Oui. Les séances sont adaptées : mouvements simplifiés, charges ajustées, coach présent du début à la fin." } },
  { "@type": "Question", name: "Tout le monde fait-il la même chose ?", acceptedAnswer: { "@type": "Answer", text: "Le cadre est identique, mais les charges et adaptations changent selon le niveau de chaque adhérent." } },
  { "@type": "Question", name: "Le WOD est-il obligatoire ?", acceptedAnswer: { "@type": "Answer", text: "Le coach peut toujours adapter les exercices selon la mobilité, l'expérience ou la fatigue du jour." } },
  { "@type": "Question", name: "Peut-on faire uniquement de l'Open Gym ?", acceptedAnswer: { "@type": "Answer", text: "Cela dépend des objectifs et des formules choisies. Parlez-en à un coach pour trouver l'organisation la plus adaptée." } },
  { "@type": "Question", name: "Quelle différence entre Open Gym et cours ?", acceptedAnswer: { "@type": "Answer", text: "Le cours est structuré et coaché collectivement. L'Open Gym laisse davantage d'autonomie pour suivre une programmation personnelle." } },
];

const jsonLd = [
  { "@context": "https://schema.org", "@type": "Article", headline: "Comment se passe un entraînement CrossFit ?", description: "Warm up, skill, renforcement, WOD et Open Gym : découvrez comment se déroule une séance chez CrossFit Chistera.", author: { "@type": "Organization", name: "CrossFit Chistera" }, publisher: { "@type": "Organization", name: "CrossFit Chistera" }, mainEntityOfPage: URL, inLanguage: "fr-FR" },
  { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faq },
  { "@context": "https://schema.org", "@type": "SportsActivityLocation", name: "CrossFit Chistera", url: "https://crossfitchistera.fr/", address: { "@type": "PostalAddress", addressLocality: "Saint-Pierre-de-Chandieu", addressRegion: "Rhône", addressCountry: "FR" }, sport: "CrossFit" },
];

const etapes = [
  { icon: Users, title: "1. Briefing", desc: "Le coach présente l'objectif, les mouvements, les adaptations, les charges et les consignes techniques. On comprend pourquoi on travaille et comment progresser." },
  { icon: Flame, title: "2. Warm up", desc: "Échauffement complet : cardio, activation musculaire, mobilité, préparation des mouvements du jour. Rameur, Echo Bike, élastiques, gainage, squats." },
  { icon: Dumbbell, title: "3. Skill ou renforcement", desc: "Travail technique (haltéro, gymnastique, double unders, handstand) ou renforcement (back squat, deadlift, strict press, gainage). Charges adaptables." },
  { icon: Timer, title: "4. WOD", desc: "Workout Of the Day. AMRAP, EMOM, For Time, intervalles, team WOD ou Hybrid Training. Cardio, renforcement, haltéro, wall balls, kettlebells, burpees, box jumps…" },
  { icon: Activity, title: "5. Récupération", desc: "Mobilité, respiration, débrief avec le coach, et souvent quelques échanges entre adhérents. La box fonctionne aussi comme une communauté." },
];

export default function SeanceCrossfit() {
  return (
    <>
      <SEO
        title="Comment se passe un entraînement CrossFit ?"
        description="Warm up, skill, renforcement, WOD et Open Gym : découvrez comment se déroule une séance chez CrossFit Chistera."
        canonical={URL}
        jsonLd={withArticleImages(jsonLd, IMAGES)}
          ogImages={IMAGES}
      />
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-32 md:pb-20">
          <article className="container mx-auto px-4 max-w-4xl">
            <BlogHeroImage slug={SLUG} />
            <nav aria-label="Fil d'Ariane" className="mb-6 text-sm text-muted-foreground flex items-center flex-wrap gap-1">
              <Link to="/" className="inline-flex items-center gap-1 hover:text-foreground"><ArrowLeft className="w-3 h-3" /> Accueil</Link>
              <ChevronRight className="w-3 h-3" />
              <span>Blog</span>
              <ChevronRight className="w-3 h-3" />
              <span className="text-foreground">Une séance type</span>
            </nav>

            <header className="mb-10">
              <span className="inline-flex items-center gap-2 text-xs font-semibold text-gold tracking-wider mb-3 font-body normal-case uppercase">
                <Activity className="w-4 h-4" /> Coulisses · Lecture ~ 8 min
              </span>
              <h1 className="text-4xl md:text-6xl text-foreground mb-6 font-heading">Comment se déroule une séance chez CrossFit Chistera ?</h1>
              <p className="text-lg text-foreground/85 leading-relaxed mb-4">
                Quand on découvre le CrossFit, on imagine souvent une séance très intense, des athlètes ultra performants, des mouvements compliqués, une ambiance de compétition. La réalité est beaucoup plus progressive… et beaucoup plus encadrée.
              </p>
              <p className="text-lg text-foreground/85 leading-relaxed mb-8">
                Chez CrossFit Chistera, une séance suit généralement une structure claire : accueil, warm up, skill ou renforcement, WOD, récupération et échanges avec les coachs et les adhérents.
              </p>
              <BlogCTA variant="hero" />
            </header>

            <div className="not-prose grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-14">
              {etapes.map((e) => (
                <div key={e.title} className="border border-border bg-card rounded-lg p-5">
                  <e.icon className="w-6 h-6 text-gold mb-3" />
                  <p className="font-heading uppercase tracking-wider text-sm text-foreground mb-2">{e.title}</p>
                  <p className="text-xs font-body normal-case text-foreground/80 leading-relaxed">{e.desc}</p>
                </div>
              ))}
            </div>

            <div className="prose prose-invert max-w-none prose-headings:font-heading prose-headings:text-foreground prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:text-foreground/85 prose-p:leading-relaxed prose-a:text-gold hover:prose-a:underline prose-strong:text-foreground prose-li:text-foreground/85">

              <h2>Une séance coachée du début à la fin</h2>
              <p>La première différence avec une salle classique, c'est <strong>le coaching</strong>. Quand vous arrivez : un coach est présent, les mouvements sont expliqués, les charges sont adaptées, les exercices sont ajustés selon votre niveau. Vous n'êtes jamais seul.</p>

              <h2>Étape 1 : le briefing</h2>
              <p>Le coach présente l'objectif de séance, les mouvements, les adaptations, les charges et les consignes techniques. Cela permet de comprendre pourquoi on travaille, comment progresser et comment adapter les exercices. Pour décoder le vocabulaire utilisé, gardez le <Link to="/lexique">lexique CrossFit</Link> sous la main.</p>

              <h2>Étape 2 : le warm up</h2>
              <p>Le warm up correspond à l'échauffement. Mais dans une box CrossFit, ce n'est pas juste « courir quelques minutes ». Il sert à préparer le cardio, activer les muscles, améliorer la mobilité, préparer les mouvements du jour et réduire le risque de blessure.</p>
              <p>On peut y retrouver rameur, Echo Bike, mobilité, activation gainage, squats, élastiques, petits circuits dynamiques.</p>

              <h2>Étape 3 : le skill ou le renforcement</h2>
              <h3>Le skill</h3>
              <p>Le skill correspond au travail technique. On peut y travailler haltérophilie, gymnastique, double unders, handstand, tractions, mobilité. Le but : <strong>améliorer la qualité du mouvement</strong>.</p>
              <h3>Le renforcement</h3>
              <p>Selon les jours, la séance peut inclure back squat, front squat, deadlift, strict press, gainage, travail unilatéral. Les charges sont toujours adaptables. Pour estimer vos pourcentages de travail, le <Link to="/blog/calculateur-charges-crossfit-1rm">calculateur 1RM</Link> est un bon repère.</p>

              <h2>Étape 4 : le WOD</h2>
              <p>Le WOD signifie <strong>Workout Of the Day</strong> — c'est la partie la plus connue du CrossFit. Les formats varient énormément : AMRAP, EMOM, For Time, intervalles, team WOD, <Link to="/cours/hybrid-training">Hybrid Training</Link>.</p>
              <p>Le WOD peut mélanger cardio, renforcement, haltérophilie, rameur, wall balls, kettlebells, burpees, box jumps. Tout est adaptable selon le niveau, la mobilité, l'expérience et la fatigue.</p>

              <h2>Étape 5 : récupération et échanges</h2>
              <p>Après le WOD : récupération, mobilité, respiration, débrief. Et souvent aussi quelques discussions avec les autres adhérents. Parce que la box fonctionne aussi comme une communauté.</p>

              <h2>Qu'est-ce que l'Open Gym ?</h2>
              <p>L'Open Gym correspond à un créneau plus libre. Contrairement à un cours classique, il n'y a pas forcément une séance collective identique : chacun peut travailler ses objectifs, refaire certains mouvements, suivre sa programmation de compétition.</p>
              <p>L'Open Gym permet notamment de pratiquer des skills, refaire un mouvement technique, travailler son cardio, préparer une compétition, compléter un autre sport ou suivre une programmation personnelle.</p>

              <h2>Open Gym : liberté… mais cadre structuré</h2>
              <p>L'Open Gym ne signifie pas faire n'importe quoi seul dans son coin. Les adhérents utilisent les racks, rameurs, Echo Bike, kettlebells, barres olympiques, sleds et espaces mobilité. Et l'ambiance reste très communautaire.</p>

              <h2>Une séance adaptée aux débutants</h2>
              <p>Chez CrossFit Chistera : les mouvements sont adaptés, les charges sont ajustées, les coachs expliquent, les groupes restent humains. Le but est de <strong>progresser durablement</strong>, pas de mettre quelqu'un en difficulté inutilement. Si vous débutez, lisez aussi notre <Link to="/blog/guide-complet-crossfit">guide complet du CrossFit</Link>.</p>

              <h2>Une ambiance familiale</h2>
              <p>Les adhérents parlent souvent de bienveillance, d'encouragements, de petits groupes, d'ambiance familiale. Les gens se connaissent, s'encouragent, progressent ensemble. Et cela change énormément l'expérience du sport — voir les <Link to="/blog/avis-crossfit-chistera">avis des adhérents</Link>.</p>
            </div>

            <div className="my-12">
              <BlogCTA />
            </div>

            <div className="prose prose-invert max-w-none prose-headings:font-heading prose-headings:text-foreground prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:text-foreground/85 prose-p:leading-relaxed prose-a:text-gold hover:prose-a:underline prose-strong:text-foreground prose-li:text-foreground/85">

              <h2>FAQ — Séance CrossFit et Open Gym</h2>
              {faq.map((q) => (
                <div key={q.name}>
                  <h3>{q.name}</h3>
                  <p>{q.acceptedAnswer.text}</p>
                </div>
              ))}
            </div>

            <aside className="border-l-4 border-gold bg-charcoal/40 px-6 py-5 mt-12 rounded-r-md">
              <p className="text-sm uppercase tracking-wider text-gold font-heading mb-3">En résumé</p>
              <p className="text-foreground/90 font-body normal-case leading-relaxed">
                Une séance chez CrossFit Chistera dure environ une heure, suit une structure claire (briefing, warm up, skill ou renforcement, WOD, récupération) et reste totalement adaptable. Le coach est présent du début à la fin. L'Open Gym apporte de la liberté pour ceux qui veulent travailler leurs objectifs personnels. Et l'ambiance fait souvent toute la différence.
              </p>
            </aside>

            <div className="mt-12">
              <BlogCTA variant="hero" />
            </div>

            <section className="mt-16 border-t border-border pt-10">
              <h2 className="text-2xl font-heading text-foreground mb-6">À lire aussi</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Link to="/charges" className="border border-border bg-card rounded-lg p-5 hover:border-gold transition-colors">
                  <Calculator className="w-5 h-5 text-gold mb-2" />
                  <p className="font-heading uppercase tracking-wider text-sm text-foreground">Calculateur de charges</p>
                </Link>
                <Link to="/lexique" className="border border-border bg-card rounded-lg p-5 hover:border-gold transition-colors">
                  <BookOpen className="w-5 h-5 text-gold mb-2" />
                  <p className="font-heading uppercase tracking-wider text-sm text-foreground">Lexique CrossFit</p>
                </Link>
                <Link to="/blog/guide-complet-crossfit" className="border border-border bg-card rounded-lg p-5 hover:border-gold transition-colors">
                  <Target className="w-5 h-5 text-gold mb-2" />
                  <p className="font-heading uppercase tracking-wider text-sm text-foreground">Guide complet CrossFit</p>
                </Link>
                <Link to="/cours/hybrid-training" className="border border-border bg-card rounded-lg p-5 hover:border-gold transition-colors">
                  <Flame className="w-5 h-5 text-gold mb-2" />
                  <p className="font-heading uppercase tracking-wider text-sm text-foreground">Hybrid Training</p>
                </Link>
              </div>
            </section>
            <BlogCTA single primaryLabel="Réserver mon essai gratuit" />

          </article>
        </main>
        <BlogStickyCTA />
        <Footer />
      </div>
    </>
  );
}
